/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Content\Modals\ModalCompany\ModalCompany.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Monday January 15th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday January 15th 2024 3:45:38 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { FormEvent, Fragment, MouseEventHandler, useEffect, useState } from "react";
import MyModal from "Content/Modals/MyModal";
import {Form, Button} from "react-bootstrap";
import translator from "MultiLanguage/TranslationWrapper";
import Styles from "Content/Modals/ModalPartner/ModalPartner.module.css";
import LoadingScreen from "Content/LoadingScreen/LoadingScreen";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import { CompanyDataType, CompanyDataUpdate } from "Type/company.type";
import { TextField } from "@mui/material";
import { formatSiren } from "Utils/company.utils";

type UpdatableCompany = CompanyDataUpdate & {[x: string]: string | undefined | null | File};

type ModalProps = {
    show: boolean;
    onClose: MouseEventHandler<HTMLDivElement>;
    closeModal: MouseEventHandler;
    title: string;
    submitFunc: Function;
    data: UpdatableCompany;
    CompanyID?: string,
    updateCompany?: Function,
};

function ModalCompany(props: ModalProps) {
    const [name, setName] = useState(props.data.Name ?? "");
    const [phone, setPhone] = useState(props.data.StandardPhoneNumber ?? "");
    const [address, setAddress] = useState(props.data.Address ?? "");
    const [postalCode, setPostalCode] = useState(props.data.PostalCode ?? "");
    const [city, setCity] = useState(props.data.City ?? "");
    const [state, setState] = useState(props.data.State ?? "");
    const [country, setCountry] = useState(props.data.Country ?? "");
    const [siren, setSiren] = useState(props.data.Siren ? formatSiren(props.data.Siren) : "");
    const [update, setUpdate] = useState({});
    const [isSendable, setIsSendable] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const [isOkay, setIsOkay] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);

    useEffect(() => {
        let tmp: UpdatableCompany = {Name: name, Siren: siren, StandardPhoneNumber: phone, Address: address, PostalCode: postalCode, City: city, State: state, Country: country};
        let tmpItems = Object.entries(tmp);
        if (props.CompanyID) {
            let diff: UpdatableCompany = {};
            for (let item of tmpItems)
                if (item[1] !== props.data[item[0]])
                    diff[item[0]] = item[1];

            if (Object.keys(diff).length > 0) {
                setIsSendable(true);
                setUpdate(diff);
            } else {
                setIsSendable(false);
                setUpdate({});
            }
        } else {
            if (tmpItems.every(val => val[1] !== undefined))
                setIsSendable(true);
            else
                setIsSendable(false);
        }
    }, [name, siren, phone, address, postalCode, city, state, country]);

    const HandleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsOkay(false);
        setSpinner(true);
        const afterCall = (isOkay: boolean = false) => {
            setIsOkay(isOkay);
            setOpenModal(true);
            setSpinner(false);
        };
        const data: CompanyDataUpdate = {
            Name: name,
            Siren: siren,
            StandardPhoneNumber: phone,
            Address: address,
            PostalCode: postalCode,
            City: city,
            State: state,
            Country: country,
            // KBIS: kbis,
        };
        if (props.CompanyID) {
            props.submitFunc(props.CompanyID, update).then(() => {
                if (props.updateCompany)
                    props.updateCompany(data);
                afterCall(true);
            }).catch(() => {afterCall()});
        } else {
            props.submitFunc(data).then((res: {message: CompanyDataType}) => {
                if (props.updateCompany)
                    props.updateCompany(res.message);
                afterCall(true);
            }).catch(() => {afterCall()});
        }
    };

    const handleSirenInput = (val: string) => {
        const formattedValue = formatSiren(val);
        setSiren(formattedValue);
    };

    return(
        <Fragment>
            <MyModal closeModal={props.closeModal} show={props.show} addCloseCross={true} onClose={props.onClose} footer={(() => { return (<></>); })()} title={props.title} className={`${Styles.modal}`}>
                {
                    spinner ?
                        <div className={`${Styles.spinnerHolder}`}>
                            <LoadingScreen />
                        </div>
                        :null
                }
                <Form className={`${Styles.formHolder}`} onSubmit={HandleSubmit}>
                    <TextField label={translator.decode("dynamic.name")} onChange={(e) => setName(e.target.value)} value={name} />
                    <TextField label={translator.decode("dynamic.siren")} onChange={(e) => handleSirenInput(e.target.value)} value={siren} inputProps={{ maxLength: 17 }} />
                    <TextField label={translator.decode("dynamic.standard")} onChange={(e) => setPhone(e.target.value)} value={phone} />
                    <TextField label={translator.decode("dynamic.address")} onChange={(e) => setAddress(e.target.value)} value={address} />
                    <TextField label={translator.decode("dynamic.postalcode")} onChange={(e) => setPostalCode(e.target.value)} value={postalCode} />
                    <TextField label={translator.decode("dynamic.city")} onChange={(e) => setCity(e.target.value)} value={city} />
                    <TextField label={translator.decode("dynamic.state")} onChange={(e) => setState(e.target.value)} value={state} />
                    <TextField label={translator.decode("dynamic.country")} onChange={(e) => setCountry(e.target.value)} value={country} />
                    <Button disabled={!isSendable} className="mb-3" type="submit">{props.CompanyID ? translator.decode("dynamic.modify") : translator.decode("dynamic.create")}</Button>
                </Form>
            </MyModal>
            <ModalConfirmation show={openModal} onClose={() => setOpenModal(false)} closeModal={() => setOpenModal(false)} isOkay={isOkay} />
        </Fragment>
    )
}

export default ModalCompany;
